/**
 * The value model is deliberately independent from pricing. A pricing model
 * answers “what does Prentus charge?”; this model answers “what opportunity
 * could the institution create?” using the same value drivers for either
 * quote framework.
 */
import type { CustomerSegment } from '../types'
import { houseNoun } from '../lib/house-noun'

export const SHARED_VALUE_MODEL_VERSION = 'shared-value-v1'

export type SharedValueComponentId =
  | 'retained_revenue'
  | 'enrollment_or_completion'
  | 'recruitment_or_replacement'
  | 'team_capacity'
  | 'career_outcomes'

export interface SharedValueInputs {
  /** Common student/jobseeker context. Used when direct counts are not supplied. */
  learnerCount?: number
  revenuePerLearner?: number
  retentionLiftPercent?: number
  improvementLearners?: number
  improvementLiftPercent?: number

  /** Direct value inputs are useful when the customer has supplied a metric. */
  retainedLearners?: number
  retainedRevenue?: number
  enrollmentOrCompletionValue?: number

  /** Only one acquisition-cost lever is applied to prevent double counting. */
  avoidedRecruitmentCount?: number
  avoidedRecruitmentUnitCost?: number
  avoidedReplacementCount?: number
  avoidedReplacementUnitCost?: number

  /** Either a direct value or weekly hours × loaded hourly cost. */
  teamCapacityValue?: number
  weeklyHoursSaved?: number
  loadedHourlyRate?: number

  additionalOutcomes?: number
  valuePerOutcome?: number
}

export interface SharedValueComponent {
  id: SharedValueComponentId
  label: string
  amount: number
  assumption: string
  sourceInputs: string[]
}

export interface SharedValueResult {
  version: typeof SHARED_VALUE_MODEL_VERSION
  annualValue: number
  components: SharedValueComponent[]
  assumptions: string[]
}

function nonNegative(value: number | undefined): number {
  return Number.isFinite(value) ? Math.max(0, value || 0) : 0
}

/**
 * Calculate annual opportunity value using only applicable, non-overlapping
 * components. Acquisition cost intentionally prefers recruitment over
 * replacement when both are present: they describe the same avoided seat and
 * must never be added together.
 */
export function calculateSharedValue(input: SharedValueInputs, segment?: CustomerSegment | null): SharedValueResult {
  const learnerCount = nonNegative(input.learnerCount)
  const revenuePerLearner = nonNegative(input.revenuePerLearner)
  const retainedLearners = input.retainedLearners === undefined
    ? learnerCount * nonNegative(input.retentionLiftPercent) / 100
    : nonNegative(input.retainedLearners)
  const retainedRevenue = input.retainedRevenue === undefined
    ? retainedLearners * revenuePerLearner
    : nonNegative(input.retainedRevenue)
  const improvementLearners = input.improvementLearners === undefined
    ? learnerCount * nonNegative(input.improvementLiftPercent) / 100
    : nonNegative(input.improvementLearners)
  const enrollmentOrCompletionValue = input.enrollmentOrCompletionValue === undefined
    ? improvementLearners * revenuePerLearner
    : nonNegative(input.enrollmentOrCompletionValue)

  const components: SharedValueComponent[] = []
  const assumptions: string[] = []

  if (retainedRevenue > 0) {
    components.push({
      id: 'retained_revenue',
      label: 'Retained tuition or student revenue',
      amount: retainedRevenue,
      assumption: input.retainedRevenue === undefined
        ? `${retainedLearners.toLocaleString()} retained students × $${Math.round(revenuePerLearner).toLocaleString()} revenue per student.`
        : 'Customer-provided retained revenue; not recomputed from pricing.',
      sourceInputs: input.retainedRevenue === undefined ? ['retainedLearners', 'revenuePerLearner'] : ['retainedRevenue'],
    })
  }

  if (enrollmentOrCompletionValue > 0) {
    components.push({
      id: 'enrollment_or_completion',
      label: 'Enrollment or completion improvement',
      amount: enrollmentOrCompletionValue,
      assumption: input.enrollmentOrCompletionValue === undefined
        ? `${improvementLearners.toLocaleString()} additional students × $${Math.round(revenuePerLearner).toLocaleString()} revenue per student.`
        : 'Customer-provided enrollment or completion value.',
      sourceInputs: input.enrollmentOrCompletionValue === undefined ? ['improvementLearners', 'revenuePerLearner'] : ['enrollmentOrCompletionValue'],
    })
  }

  // Recruitment and replacement are alternative ways of valuing the same
  // avoided seat. Prefer the explicit recruitment input when both exist.
  const hasRecruitment = input.avoidedRecruitmentCount !== undefined || input.avoidedRecruitmentUnitCost !== undefined
  const acquisitionKind = hasRecruitment ? 'recruitment' : 'replacement'
  const acquisitionCount = hasRecruitment
    ? nonNegative(input.avoidedRecruitmentCount)
    : nonNegative(input.avoidedReplacementCount)
  const acquisitionUnitCost = hasRecruitment
    ? nonNegative(input.avoidedRecruitmentUnitCost)
    : nonNegative(input.avoidedReplacementUnitCost)
  const acquisitionValue = acquisitionCount * acquisitionUnitCost
  if (acquisitionValue > 0) {
    components.push({
      id: 'recruitment_or_replacement',
      label: acquisitionKind === 'recruitment' ? 'Avoided recruitment cost' : 'Avoided replacement cost',
      amount: acquisitionValue,
      assumption: `${acquisitionCount.toLocaleString()} avoided ${acquisitionKind} seats × $${Math.round(acquisitionUnitCost).toLocaleString()} each.`,
      sourceInputs: acquisitionKind === 'recruitment'
        ? ['avoidedRecruitmentCount', 'avoidedRecruitmentUnitCost']
        : ['avoidedReplacementCount', 'avoidedReplacementUnitCost'],
    })
  }

  const teamCapacityValue = input.teamCapacityValue === undefined
    ? nonNegative(input.weeklyHoursSaved) * 52 * nonNegative(input.loadedHourlyRate)
    : nonNegative(input.teamCapacityValue)
  if (teamCapacityValue > 0) {
    components.push({
      id: 'team_capacity',
      label: 'Team-capacity savings',
      amount: teamCapacityValue,
      assumption: input.teamCapacityValue === undefined
        ? `${nonNegative(input.weeklyHoursSaved).toLocaleString()} hours saved each week × 52 × $${Math.round(nonNegative(input.loadedHourlyRate)).toLocaleString()} loaded hourly cost.`
        : 'Customer-provided annual team-capacity value.',
      sourceInputs: input.teamCapacityValue === undefined ? ['weeklyHoursSaved', 'loadedHourlyRate'] : ['teamCapacityValue'],
    })
  }

  const outcomeCount = nonNegative(input.additionalOutcomes)
  const outcomeValue = outcomeCount * nonNegative(input.valuePerOutcome)
  if (outcomeValue > 0) {
    components.push({
      id: 'career_outcomes',
      label: 'Career-outcome value',
      amount: outcomeValue,
      assumption: `${outcomeCount.toLocaleString()} additional annual outcomes × $${Math.round(nonNegative(input.valuePerOutcome)).toLocaleString()} estimated value per outcome.`,
      sourceInputs: ['additionalOutcomes', 'valuePerOutcome'],
    })
  }

  if (learnerCount > 0) {
    const noun = houseNoun(segment)
    const label = noun.singular.charAt(0).toUpperCase() + noun.singular.slice(1)
    assumptions.push(`${label} context: ${learnerCount.toLocaleString()} ${noun.plural}.`)
  }
  if (input.retentionLiftPercent !== undefined) assumptions.push(`Retention lift assumption: ${nonNegative(input.retentionLiftPercent)} percentage points.`)
  if (input.improvementLiftPercent !== undefined) assumptions.push(`Enrollment or completion lift assumption: ${nonNegative(input.improvementLiftPercent)} percentage points.`)
  if (hasRecruitment && (input.avoidedReplacementCount !== undefined || input.avoidedReplacementUnitCost !== undefined)) {
    assumptions.push('Recruitment cost is used instead of replacement cost so acquisition value is not double-counted.')
  }
  if (components.length === 0) assumptions.push('No value component is included until a supported metric is entered.')
  for (const component of components) {
    if (!assumptions.includes(component.assumption)) assumptions.push(component.assumption)
  }

  return {
    version: SHARED_VALUE_MODEL_VERSION,
    annualValue: components.reduce((total, component) => total + component.amount, 0),
    components,
    assumptions,
  }
}
